import { StatusData, ITenant, dollars, dollarsToNum, errorToString, LoanCalculations } from "@oneethos/shared"
import { useEffect, useRef } from "react"
import api from "../api-client"
import { toast } from "react-toastify"
import LoanOptionCard from "./loan-option-card"
import { TenantState } from "../reducers/tenantReducer"
import WaveDotsLoader from "./loader"
import useLoanData from "../hooks/useLoanData"

type SelectedLoanTermProps = {
  project: StatusData
  tenant: TenantState
  onChange: (project: StatusData) => void
  hideDisclaimer?: boolean
}

export const SelectedLoanTerm = ({
  project,
  tenant,
  onChange,
  hideDisclaimer = false
}: SelectedLoanTermProps) => {
  const pollingInterval = useRef(null)
  const loanData = useLoanData(project)

  const fetchData = () => {
    api.getProject(project.lookup).then(data => {
      onChange(data)
    }).catch(ex => toast.error(errorToString(ex), { autoClose: false }))
  }

  useEffect(() => {
    if (!project.productId && !pollingInterval.current) {
      pollingInterval.current = setInterval(fetchData, 10_000)
    } else {
      clearInterval(pollingInterval.current)
    }

    return () => clearInterval(pollingInterval.current)
  }, [project.productId])


  if (!project.productId) {
    return <div className="awaiting-loan-term alert alert-info">
      <div className="loader-wrap">
        <WaveDotsLoader dotCount={4} duration={10} />
      </div>
      <div className="alt-label">Waiting for customer to select a loan term</div>
    </div>
  }

  const product = tenant.config.products.find(p => p.id === project.productId)

  if (!product) {
    return <div className="alert alert-warning">
      Could not find product, cannot display loan payment information.
    </div>
  }

  const payments = LoanCalculations.paymentsForProduct(
    product,
    project.loanAmount,
    project.installationAddress,
    project.projectType,
    project.loanModFee || 0,
    undefined,
    Array.isArray(project.fees) ? project.fees : undefined
  )

  const apr = LoanCalculations.apr(
    dollarsToNum(project.loanAmount),
    product.term,
    payments.withAutoPay
  )
  const showDisclaimer = !hideDisclaimer && project.projectType === "residential"

  return <div className="form-group loan-options">
    {project.incentiveSelected ?
      <div className="card loan-option-card pt-4">
        <div className="loan-option-card-details">
          <div className="d-flex justify-content-between border-bottom pb-3">
            <div className="monthly-payment">
              <span className="mp-amt">{dollars(loanData.mcgb.incentiveMonthlyPayment)}</span>/mo*
            </div>
            <div className="text-small text-end d-flex align-items-end">(Years 1-15)</div>
          </div>
          <div className="d-flex justify-content-between py-2">
            <div className="monthly-payment">
              <span className="mp-amt">{dollars(loanData.mcgb.standardMonthlyPayment)}</span>/mo*
            </div>
            <div className="text-small text-end d-flex align-items-end">(After Year 15)</div>
          </div>
          <div className="d-flex flex-column my-3">
            <p><strong>{LoanCalculations.calculateLoanTermInYears(product.term)} years</strong></p>
            <p><strong>No Dealer Fees</strong></p>
            <p><strong>{product.interestOnlyPeriod} Months No Payments</strong></p>
          </div>
          <div className="mt-3">
            <img src={project.incentive.financingPartnerLogo} alt="financing partner logo" className="logo m-0" />
          </div>
        </div>
      </div> :
      <LoanOptionCard
        financingTenant={tenant.config as ITenant}
        loanTermYears={LoanCalculations.calculateLoanTermInYears(product.term)}
        interestWithAutoPay={product.interestRateWithAutopay}
        estimatedPaymentWithAutopay={dollars(payments.withAutoPay)}
        estimatedPaymentWithOutAutopay={dollars(payments.withoutAutoPay)}
        isConsumerView={false}
        isSelected={product.id === project.productId}
        loanTermMonths={product.term}
        interestOnlyPeriod={product.interestOnlyPeriod}
        apr={Number(apr.toFixed(3))}
        isResidential={project.projectType === "residential"}
      />}
    {showDisclaimer && project.incentiveSelected && (
      <p className="mt-3 fst-italic small">
        *Auto-pay is required to qualify for loans in partnership with Montgomery County Green Bank.
      </p>
    )}
    {showDisclaimer && !project.incentiveSelected && (
      <p className="mt-3 fst-italic small">
        *{tenant.config.name} offers a discount to clients that opt into, and maintain,
        automatic monthly payments. The bill amount will automatically be withdrawn
        from the client's checking account.
      </p>)
    }
  </div>
}

export default SelectedLoanTerm